import { isPlainPythonQuotedStringLiteral, splitTopLevelByComma } from '../../utils/pythonSourceScanUtils';

type TokenizeArgsOptions = {
  ignoreLineComments?: boolean;
};

export function tokenizeArgs(argsStr: string, options: TokenizeArgsOptions = {}): string[] {
  return splitTopLevelByComma(argsStr, options);
}

export function toJsValue(raw: string): { type: string; value: unknown } {
  const trimmed = raw.trim();
  if (/^(True|False)$/i.test(trimmed)) return { type: 'bool', value: /^True$/i.test(trimmed) };
  if (/^None$/i.test(trimmed)) return { type: 'none', value: null };
  if (/^[+-]?(\d+\.?\d*|\.\d+)$/.test(trimmed)) return { type: trimmed.includes('.') ? 'float' : 'integer', value: parseFloat(trimmed) };
  if (isPlainPythonQuotedStringLiteral(trimmed)) {
    // Decode escapes to their real characters so the round-trip with escapePythonStringLiteral is
    // lossless (e.g. `\n` becomes a newline, not the letter `n`).
    const unq = trimmed.slice(1, -1).replace(/\\([\\'"nrt])/g, (_match, ch: string) => {
      switch (ch) {
        case 'n':
          return '\n';
        case 't':
          return '\t';
        case 'r':
          return '\r';
        default:
          return ch; // \\ \' \"
      }
    });
    return { type: 'string', value: unq };
  }
  if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
    const inner = trimmed.slice(1, -1);
    const elements = tokenizeArgs(inner, { ignoreLineComments: true }).map((tok) => toJsValue(tok).value);
    return { type: 'tuple', value: elements };
  }
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    const inner = trimmed.slice(1, -1);
    const elements = inner.trim().length === 0 ? [] : tokenizeArgs(inner, { ignoreLineComments: true }).map((tok) => toJsValue(tok).value);
    return { type: 'list', value: elements };
  }
  return { type: 'identifier', value: trimmed };
}

/**
 * Parses a Python list-of-tuples literal (e.g. `[(1, "Ready"), (2, "Go")]`) into an array
 * of rows, each row an array of member values. Non-list / unparseable input yields `[]`.
 */
export function parseSequenceTupleLiteral(literal: string): unknown[][] {
  const { value } = toJsValue(String(literal ?? '').trim());
  if (!Array.isArray(value)) return [];
  return value.map((row) => (Array.isArray(row) ? row : [row]));
}

/** Whether a parsed value is a list whose every element is a tuple of exactly `arity` members. */
export function sequenceTupleRowsMatchArity(value: unknown, arity: number): boolean {
  return Array.isArray(value) && value.every((row) => Array.isArray(row) && row.length === arity);
}

/** Whether a raw literal parses to a list of tuples each with exactly `arity` members. */
export function sequenceTupleLiteralRowsMatch(literal: string, arity: number): boolean {
  return sequenceTupleRowsMatchArity(toJsValue(String(literal ?? '').trim()).value, arity);
}

const SCALAR_LITERAL_MEMBER_TYPES = new Set(['integer', 'float', 'bool', 'none', 'string']);
const STRING_LIKE_MEMBER_TYPE = /^(string|str|text|path_name)$/i;

/** Minimal shape of a tuple member needed to type-check its token (a `FrameworkArgument` satisfies it). */
type SequenceTupleMemberLike = { type?: string };

/**
 * Whether a single tuple-member token is a scalar Python literal (integer, float, bool, None, or a
 * quoted string) as opposed to an identifier, call, operator expression, or nested collection.
 * Only scalar literals can round-trip through a plain UI text field; an identifier like `message_var`
 * is indistinguishable from the string `"message_var"` once it sits in a text input.
 */
export function isScalarLiteralToken(token: string): boolean {
  return SCALAR_LITERAL_MEMBER_TYPES.has(toJsValue(String(token ?? '').trim()).type);
}

// The escapes `toJsValue` decodes and `escapePythonStringLiteral` re-encodes losslessly.
const SIMPLE_STRING_ESCAPE_CHARS = '\\\'"ntr';

/**
 * Whether a plain quoted-string token uses only the simple escapes we round-trip losslessly
 * (`\\ \' \" \n \t \r`). Strings with other recognized Python escapes — `\xHH`, `\uHHHH`,
 * `\UHHHHHHHH`, `\N{name}`, octal `\ooo`, `\a \b \f \v \0` — are NOT decoded by `toJsValue`, so editing
 * them would re-escape the backslash and change the value (`"\x41"` = `A` → `"\\x41"` = literal `\x41`).
 * Such rows are preserved verbatim as unsupported code instead. `token` must already be a plain quoted
 * literal (i.e. `toJsValue(token).type === 'string'`).
 */
export function quotedStringHasOnlySimpleEscapes(token: string): boolean {
  const body = String(token ?? '')
    .trim()
    .slice(1, -1);
  for (let i = 0; i < body.length; i++) {
    if (body[i] === '\\') {
      const next = body[i + 1];
      if (next === undefined || !SIMPLE_STRING_ESCAPE_CHARS.includes(next)) return false;
      i++; // consume the escaped char so an escaped backslash (`\\`) doesn't mis-trigger on the next char
    }
  }
  return true;
}

/**
 * Whether `token` is a literal of the kind the member's declared type expects: an `int` member needs
 * an integer literal, a `float`/`number` member an integer or float, a string-like member a quoted
 * string (using only simple escapes), a `bool` member a boolean. Unknown/enum member types fall back
 * to accepting any scalar literal so we don't newly reject members we can't classify. This rejects
 * e.g. `True` for an `int` member (which would hydrate to lowercase `true`) and `"\x41"` for a string
 * member (which `toJsValue` can't decode, so editing it would corrupt the value).
 */
export function isLiteralTokenForMember(token: string, member: SequenceTupleMemberLike | undefined): boolean {
  const trimmed = String(token ?? '').trim();
  const tokenType = toJsValue(trimmed).type;
  const memberType = String(member?.type ?? '').toLowerCase();
  if (STRING_LIKE_MEMBER_TYPE.test(memberType)) return tokenType === 'string' && quotedStringHasOnlySimpleEscapes(trimmed);
  if (memberType === 'int' || memberType === 'integer') return tokenType === 'integer';
  if (memberType === 'float' || memberType === 'number' || memberType === 'double') return tokenType === 'integer' || tokenType === 'float';
  if (memberType === 'bool' || memberType === 'boolean') return tokenType === 'bool';
  return SCALAR_LITERAL_MEMBER_TYPES.has(tokenType);
}

/**
 * Whether a raw list-of-tuples literal can be hydrated into editable UI rows: it must be a list whose
 * every element is a tuple of exactly `members.length` members AND every member is a literal matching
 * that member's declared type. Rows that contain identifiers/expressions (`[(1, message_var)]`,
 * `[(get_n(), "A")]`) or type-mismatched literals (`[(True, "A")]` for `(int, str)`) are NOT editable —
 * a text field can't round-trip them faithfully — so they are preserved verbatim as unsupported code
 * instead. An empty list (`[]`) is editable (zero rows).
 */
export function sequenceTupleLiteralRowsAreEditable(literal: string, members: ReadonlyArray<SequenceTupleMemberLike>): boolean {
  const trimmed = String(literal ?? '').trim();
  if (!(trimmed.startsWith('[') && trimmed.endsWith(']'))) return false;
  const inner = trimmed.slice(1, -1).trim();
  if (inner === '') return true;
  const arity = members.length;
  return tokenizeArgs(inner, { ignoreLineComments: true }).every((rowTok) => {
    const row = rowTok.trim();
    if (!(row.startsWith('(') && row.endsWith(')'))) return false;
    const tokens = tokenizeArgs(row.slice(1, -1), { ignoreLineComments: true });
    return tokens.length === arity && tokens.every((token, index) => isLiteralTokenForMember(token, members[index]));
  });
}

/**
 * Whether a raw `lines`-style value is a non-empty expression that can't be hydrated into
 * editable tuple rows (e.g. `test`, `get_lines()`, `[foo()]`, `[(1, "A", extra)]`, `[(True, "A")]`, a
 * malformed literal). An empty string or an empty list (`[]`) is not "unsupported" — it just means
 * zero rows. When `members` is given, a list is supported only if every element is a tuple matching
 * the member arity AND each member is a literal of the expected type, so mixed/wrong-shape/wrong-type
 * lists and rows containing identifiers/expressions are preserved verbatim instead of being silently
 * rewritten on save.
 */
export function isUnsupportedSequenceTupleLiteral(raw: string, members?: ReadonlyArray<SequenceTupleMemberLike>): boolean {
  const value = String(raw ?? '').trim();
  if (value === '') return false;
  if (/^\[\s*\]$/.test(value)) return false;
  if (members) return !sequenceTupleLiteralRowsAreEditable(value, members);
  return parseSequenceTupleLiteral(value).length === 0;
}
